import {ComponentRef, Directive, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewContainerRef} from '@angular/core';

/**
 * CustomComponentHostDirective
 *
 * This directive acts as a dynamic component host, enabling the creation and management
 * of components at runtime while facilitating two-way data binding and event propagation.
 * It handles component lifecycle, input/output binding, and proper cleanup.
 */
@Directive({
	selector: '[appCustomComponentHost]'
})
export class CustomComponentHostDirective implements OnInit, OnChanges, OnDestroy {
	/**
	 * The component class to be dynamically instantiated and rendered
	 */
	@Input() component: any;

	/**
	 * The initial value passed to the dynamically created component
	 */
	@Input() value: any;

	/**
	 * EventEmitter that propagates changes from the child component to the parent
	 */
	@Output() valueChange = new EventEmitter<any>();

	/**
	 * Optional additional input properties to be passed to the dynamically created component
	 * This allows flexible configuration without modifying the directive
	 */
	@Input() extraInputs: { [key: string]: any } = {};

	/**
	 * Reference to the dynamically created component instance
	 * Allows direct access to component methods and properties
	 */
	private componentRef?: ComponentRef<any>;

	/**
	 * Subscription to the child component's valueChange EventEmitter
	 * Stored for proper cleanup during directive destruction
	 */
	private subscription: any;

	constructor(private viewContainerRef: ViewContainerRef) {}

	/**
	 * Angular lifecycle hook: Initialization
	 * Triggered when the directive is initialized
	 * Initiates the dynamic component loading process
	 */
	ngOnInit() {
		this.loadComponent();
	}

	/**
	 * Angular lifecycle hook: Detect input property changes
	 * Triggered whenever any @Input property changes after component initialization
	 * Updates the dynamically created component with new values from parent
	 *
	 * @param changes - Object containing changed properties and their current/previous values
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (this.componentRef) {
			// Update the 'value' input property if it has changed and is not the initial assignment
			if (changes['value'] && !changes['value'].firstChange) {
				this.componentRef.instance.value = this.value;
			}

			// Update all additional input properties from the extraInputs object
			if (changes['extraInputs']) {
				Object.keys(this.extraInputs).forEach(key => {
					this.componentRef!.instance[key] = this.extraInputs[key];
				});
			}

			// Manually trigger change detection on the dynamically created component
			// Ensures the component's view reflects all property updates
			this.componentRef.changeDetectorRef.detectChanges();
		}
	}

	/**
	 * Dynamically creates and configures a component instance
	 * Manages component instantiation, property binding, and event subscription
	 *
	 * This method:
	 * 1. Clears any previously rendered components from the view container
	 * 2. Creates a new component instance dynamically
	 * 3. Binds input properties (value and extraInputs) to the new component
	 * 4. Subscribes to the component's output events to forward them to the parent
	 * 5. Triggers change detection to render the component
	 */
	loadComponent() {
		if (this.component) {
			// Clear the view container to remove any previously rendered component
			this.viewContainerRef.clear();

			// Dynamically create a component instance from the provided component class
			this.componentRef = this.viewContainerRef.createComponent(this.component);


			// Bind the initial value to the dynamically created component
			this.componentRef.instance.value = this.value;

			// Iterate through all extraInputs and bind each property to the component instance
			// This provides flexibility for passing any number of custom properties
			Object.keys(this.extraInputs).forEach(key => {
				this.componentRef!.instance[key] = this.extraInputs[key];
			});

			// Subscribe to the child component's valueChange EventEmitter (if it exists)
			// This establishes the output event connection from child to parent
			if (this.componentRef.instance.valueChange) {
				this.subscription = this.componentRef.instance.valueChange.subscribe((newValue: any) => {
					// Forward the child component's event to the parent via the directive's output
					this.valueChange.emit(newValue);
				});
			}

			// Trigger change detection on the newly created component
			// Ensures the component's view is properly rendered with all bound properties
			this.componentRef.changeDetectorRef.detectChanges();
		}
	}

	/**
	 * Angular lifecycle hook: Cleanup and destruction
	 * Triggered when the directive is being destroyed
	 * Performs essential cleanup to prevent memory leaks:
	 * - Unsubscribes from event subscriptions
	 * - Destroys the dynamically created component instance
	 */
	ngOnDestroy() {
		// Unsubscribe from the child component's valueChange event to prevent memory leaks
		if (this.subscription) {
			this.subscription.unsubscribe();
		}

		// Destroy the dynamically created component instance
		// This removes the component from the DOM and releases associated resources
		if (this.componentRef) {
			this.componentRef.destroy();
		}
	}
}